import { useEffect, useRef, useState } from 'react';
import { Box, buildJourney, Journey } from './journeyPath';

/** Share of the section that must be on screen before the journey light runs (D4 amendment, WU-HOW). */
const LIVE_THRESHOLD = 0.25;
const IDLE_TIMEOUT_MS = 2000;

type IdleWindow = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

function measure(container: HTMLElement): Journey | null {
  const frame = container.getBoundingClientRect();
  if (frame.width === 0 || frame.height === 0) return null;
  const relative = (rect: DOMRect): Box => ({
    left: rect.left - frame.left,
    top: rect.top - frame.top,
    right: rect.right - frame.left,
    bottom: rect.bottom - frame.top,
  });
  const cards = Array.from(container.querySelectorAll<HTMLElement>('.send-step'));
  const badges = cards.map((card) => card.querySelector<HTMLElement>('.ui-icon-badge'));
  if (cards.length !== 5 || badges.some((badge) => badge == null)) return null;
  const badgeRects = badges.map((badge) => (badge as HTMLElement).getBoundingClientRect());
  return buildJourney({
    cards: cards.map((card) => relative(card.getBoundingClientRect())),
    nodes: badgeRects.map((rect) => ({
      x: rect.left - frame.left + rect.width / 2,
      y: rect.top - frame.top + rect.height / 2,
    })),
    badgeRadius: badgeRects[0].width / 2,
    width: frame.width,
    height: frame.height,
  });
}

function sameJourney(a: Journey | null, b: Journey | null): boolean {
  if (a == null || b == null) return a === b;
  return a.travel === b.travel && a.width === b.width && a.height === b.height;
}

/**
 * Measures the five step cards and works out the route the pointer travels through their badges (WU-HOW). It re-measures
 * when the section or a card changes size (width, language, fonts). `live` is true while the section is on
 * screen, so the pointer only runs where it can be seen. Without layout (jsdom) there is no journey, and the
 * section is just its cards: the pointer is decoration.
 *
 * The route is measured once the page is idle after load (like the globe), so it never competes with the
 * first paint: the cards are complete without it.
 */
export function useJourneyPath() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [journey, setJourney] = useState<Journey | null>(null);
  const [live, setLive] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (container == null) return undefined;
    const win = window as IdleWindow;
    let frameId = 0;
    let idleHandle: number | null = null;
    let timer: number | null = null;
    let cancelled = false;
    let resize: ResizeObserver | null = null;
    const update = () => {
      const next = measure(container);
      setJourney((previous) => (sameJourney(previous, next) ? previous : next));
    };
    const schedule = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(update);
    };
    const start = () => {
      if (cancelled) return;
      update();
      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(schedule);
        observer.observe(container);
        container.querySelectorAll('.send-step').forEach((card) => observer.observe(card));
        resize = observer;
      }
      const fonts = (document as Document & { fonts?: { ready: Promise<unknown> } }).fonts;
      fonts?.ready.then(() => {
        if (!cancelled) schedule();
      });
    };
    const whenIdle = () => {
      if (win.requestIdleCallback) idleHandle = win.requestIdleCallback(start, { timeout: IDLE_TIMEOUT_MS });
      else timer = window.setTimeout(start, 0);
    };
    if (document.readyState === 'complete') whenIdle();
    else window.addEventListener('load', whenIdle, { once: true });

    return () => {
      cancelled = true;
      window.removeEventListener('load', whenIdle);
      if (idleHandle != null) win.cancelIdleCallback?.(idleHandle);
      if (timer != null) window.clearTimeout(timer);
      window.cancelAnimationFrame(frameId);
      resize?.disconnect();
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (container == null || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      // isIntersecting stays true for any overlap, so the ratio decides (with a hair of rounding slack).
      (entries) =>
        entries.forEach((entry) =>
          setLive(entry.isIntersecting && entry.intersectionRatio >= LIVE_THRESHOLD - 0.01),
        ),
      { threshold: [0, LIVE_THRESHOLD] },
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return { containerRef, journey, live };
}
